/* ============================================================
   SOCIAL IRONY — theme.js
   Vanilla JS. No framework, no build step. Progressive enhancement only:
   every interaction degrades to normal links and forms without JS.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Small helpers ---------- */
  const qs = (selector, scope) => (scope || document).querySelector(selector);
  const qsa = (selector, scope) => Array.from((scope || document).querySelectorAll(selector));

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ---------- Money formatting (used by cart.js + product-drawer.js) ---------- */
  const formatMoney = (cents, format) => {
    if (typeof cents !== 'number') return '';
    const template = format || window.SI_MONEY_FORMAT || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withSeparator = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return template
      .replace(/\{\{\s*amount\s*\}\}/, withSeparator + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparator)
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/,
        withSeparator.replace(/\./g, ',') + ',' + parts[1]
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
        withSeparator.replace(/\./g, ',')
      );
  };

  /* ---------- Toasts ---------- */
  const toastRegion = qs('[data-si-toasts]');

  const showToast = (message, variant) => {
    if (!toastRegion || !message) return;
    const toast = document.createElement('div');
    toast.className = 'si-toast' + (variant ? ' si-toast--' + variant : '');
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    toastRegion.appendChild(toast);

    window.setTimeout(() => {
      toast.style.opacity = '0';
      window.setTimeout(() => toast.remove(), 200);
    }, 4200);
  };

  window.SI = window.SI || {};
  window.SI.showToast = showToast;
  window.SI.formatMoney = formatMoney;
  window.SI.qs = qs;
  window.SI.qsa = qsa;
  window.SI.prefersReducedMotion = prefersReducedMotion;

  /* ---------- Drawer manager ---------- */
  const overlay = qs('[data-si-overlay]');
  let activeDrawer = null;
  let lastFocused = null;

  const drawerConfig = {
    cart: { el: '[data-si-cart-drawer]', triggers: '[data-si-open-cart]' },
    search: { el: '[data-si-search-drawer]', triggers: '[data-si-open-search]' },
    nav: { el: '[data-si-nav-drawer]', triggers: '[data-si-open-nav]' },
    product: { el: '[data-si-product-drawer]', triggers: '[data-si-open-product]' }
  };

  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('si-scroll-locked', locked);
  };

  const openDrawer = (name, trigger) => {
    const config = drawerConfig[name];
    if (!config) return;

    if (activeDrawer) closeDrawer();

    const drawer = qs(config.el);
    if (!drawer) return;

    lastFocused = trigger || document.activeElement;
    drawer.hidden = false;
    if (overlay) overlay.hidden = false;

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-active');
      if (overlay) overlay.classList.add('is-active');
    });

    activeDrawer = drawer;
    lockScroll(true);

    const focusTarget =
      qs('[data-si-autofocus]', drawer) || qs(focusableSelector, drawer) || drawer;
    window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);

    qsa(config.triggers).forEach((el) => el.setAttribute('aria-expanded', 'true'));
    document.dispatchEvent(new CustomEvent('si:drawer:open', { detail: { name: name, drawer: drawer } }));
  };

  const closeDrawer = () => {
    if (!activeDrawer) return;
    const drawer = activeDrawer;
    activeDrawer = null;

    drawer.classList.remove('is-active');
    if (overlay) overlay.classList.remove('is-active');
    lockScroll(false);

    window.setTimeout(
      () => {
        drawer.hidden = true;
        if (overlay && !activeDrawer) overlay.hidden = true;
      },
      prefersReducedMotion ? 0 : 200
    );

    qsa(
      '[data-si-open-cart], [data-si-open-search], [data-si-open-nav], [data-si-open-product]'
    ).forEach((el) => el.setAttribute('aria-expanded', 'false'));

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('si:drawer:close', { detail: { drawer: drawer } }));
  };

  window.SI.openDrawer = openDrawer;
  window.SI.closeDrawer = closeDrawer;
  window.SI.getActiveDrawer = () => activeDrawer;

  /* ---------- Global click delegation ---------- */
  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest(
      '[data-si-open-cart], [data-si-open-search], [data-si-open-nav]'
    );

    if (openTrigger) {
      // Fall back to normal navigation when the matching drawer isn't rendered
      // (cart page mode, search disabled).
      if (openTrigger.hasAttribute('data-si-open-cart') && !qs('[data-si-cart-drawer]')) return;
      if (openTrigger.hasAttribute('data-si-open-search') && !qs('[data-si-search-drawer]')) return;

      event.preventDefault();
      if (openTrigger.hasAttribute('data-si-open-cart')) openDrawer('cart', openTrigger);
      if (openTrigger.hasAttribute('data-si-open-search')) openDrawer('search', openTrigger);
      if (openTrigger.hasAttribute('data-si-open-nav')) openDrawer('nav', openTrigger);
      return;
    }

    if (event.target.closest('[data-si-close-drawer]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (overlay && event.target === overlay) {
      closeDrawer();
    }
  });

  /* ---------- Keyboard: escape + simple focus trap ---------- */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) {
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab' || !activeDrawer) return;

    const focusable = qsa(focusableSelector, activeDrawer).filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------- Announcement rotation ---------- */
  const announcement = qs('[data-si-announcement]');
  if (announcement && announcement.dataset.rotateInterval) {
    const items = qsa('[data-si-announcement-item]', announcement);

    if (items.length > 1) {
      const interval = parseInt(announcement.dataset.rotateInterval, 10) || 5000;
      let index = 0;
      let timer = null;

      const rotate = () => {
        items[index].classList.remove('is-active');
        items[index].hidden = true;
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
        items[index].hidden = false;
      };

      const start = () => {
        if (timer) return;
        timer = window.setInterval(rotate, interval);
      };

      const stop = () => {
        window.clearInterval(timer);
        timer = null;
      };

      start();

      // Don't burn cycles (or drift) while the tab is in the background.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });
    }
  }

  /* ---------- Desktop nav dropdowns (click + hover intent) ---------- */
  qsa('[data-si-nav-group]').forEach((group) => {
    const toggle = qs('[data-si-nav-group-toggle]', group);
    const dropdown = qs('[data-si-nav-dropdown]', group);
    if (!toggle || !dropdown) return;

    const close = () => {
      dropdown.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    const open = () => {
      dropdown.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (dropdown.hidden) {
        open();
      } else {
        close();
      }
    });

    group.addEventListener('mouseenter', open);
    group.addEventListener('mouseleave', close);
    group.addEventListener('focusout', (event) => {
      if (!group.contains(event.relatedTarget)) close();
    });
  });

  /* ---------- Quick add from product cards ---------- */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-si-quick-add]');
    if (!form) return;

    event.preventDefault();
    const button = qs('button[type="submit"]', form);
    const originalLabel = button ? button.textContent : '';

    if (button) {
      button.disabled = true;
      button.textContent = window.SI.strings.adding;
    }

    const quickAddData = new FormData(form);

    // Ask for the drawer sections on the add itself, so the response carries the
    // updated drawer HTML. Relying on the si:cart:added listener to fetch them
    // afterwards opened the drawer one network round trip behind the mutation -
    // the same "drawer opens empty" bug the product form had.
    if (qs('[data-si-cart-drawer]')) {
      quickAddData.append('sections', 'cart-drawer-contents,cart-drawer-footer');
      quickAddData.append('sections_url', window.location.pathname);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: quickAddData
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) throw new Error(data.description || window.SI.strings.error);
          return data;
        })
      )
      .then((item) => {
        // Render before opening. renderSections lives in cart.js and is exposed on
        // window.SI; if it is missing the listener in cart.js still refreshes, just
        // one round trip later.
        if (item.sections && window.SI.renderCartSections) {
          window.SI.renderCartSections(item.sections);
        }

        if (window.SI.updateCartCount) window.SI.updateCartCount(item.item_count);

        document.dispatchEvent(
          new CustomEvent('si:cart:added', {
            detail: { item: item, source: 'quick-add', sections: item.sections }
          })
        );
        showToast(window.SI.strings.added, 'success');
        if (qs('[data-si-cart-drawer]')) openDrawer('cart');
      })
      .catch((error) => {
        showToast(error.message || window.SI.strings.error, 'error');
      })
      .finally(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      });
  });

  /* ---------- Product drawer trigger ---------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-si-open-product]');
    if (!trigger) return;

    const drawer = qs('[data-si-product-drawer]');
    if (!drawer) return; // No drawer rendered: let the plain link behave normally.

    event.preventDefault();
    openDrawer('product', trigger);
    document.dispatchEvent(
      new CustomEvent('si:product:open', {
        detail: {
          handle: trigger.dataset.productHandle,
          url: trigger.dataset.productUrl,
          drawer: drawer
        }
      })
    );
  });
})();
